/**
 * One-off 301s for URLs that are indexed or linked from outside but that the
 * app no longer answers (or answers at a second, duplicate address).
 *
 * The SAME rules live in the nginx redirect file of each server (siila.com,
 * siila.com.br, siila.com.mx); nginx is what answers first-time visitors and
 * crawlers with a real HTTP 301. This table exists because nginx never sees a
 * returning visitor's navigation: the service worker answers it from its cached
 * `index.html` (see `navigationUrls` in `ngsw-config.json`), so without a copy
 * here the redirect would apply to Googlebot and silently not apply to people.
 * When you add a rule to nginx, add it here too, and vice versa.
 *
 * Keyed by `environment.platformId` rather than hostname, so a rule follows its
 * deployment onto staging. `from` is stored DECODED (no percent-encoding) and
 * without a trailing slash, which is also how nginx matches `location =`; the
 * comparison is case-sensitive (`/lang/eN` redirects, `/lang/en` must not).
 *
 * Derived from `9.29 Rediects (1).xlsx`.
 *
 * Pure data + pure functions — no Angular, no `window` — mirroring
 * `product-catalog.ts`. Applied once, at bootstrap, by `appInitializerFactory`.
 */

export interface LegacyRedirect {
  /** Requested path: decoded, no trailing slash, no query string. */
  from: string;
  /** Target path. The query string of the request is carried over. */
  to: string;
  /** Deployment that owns `to`; omitted when it is this same deployment. */
  toPlatform?: string;
}

export const LEGACY_REDIRECTS: { [platformId: string]: LegacyRedirect[] } = {
  br: [
    {
      from: "/marketanalytics/lang",
      to: "/marketanalytics/lang/en"
    },
    {
      from: "/market-fundamentals/lang/es",
      to: "/market-fundamentals",
      toPlatform: "mx"
    },
    {
      from: "/SPOT/lang",
      to: "/SPOT/lang/en"
    },
    {
      from: "/produtos",
      to: "/products"
    },
    {
      from:
        "/news/brazil-real-estate-registry-unifies-data-and-lays-groundwork-for-new-tax-system/7977/lang/products/fii-data-pro",
      to:
        "/news/brazil-real-estate-registry-unifies-data-and-lays-groundwork-for-new-tax-system/7977/lang/en"
    },
    {
      from:
        "/news/top-five-logistics-warehouses-office-transactions-2022/257/lang/products/fii-data-pro",
      to:
        "/news/top-five-logistics-warehouses-office-transactions-2022/257/lang/en"
    },
    {
      from:
        "/noticias/atletico-mg-inicia-planejamento-vender-shopping-diamond-mall/899/lang/products/fii-data-pro",
      to:
        "/noticias/atletico-mg-inicia-planejamento-vender-shopping-diamond-mall/899"
    },
    {
      from: "/marketanalytics/lang/undefined",
      to: "/marketanalytics/lang/en"
    }
  ],
  mx: [
    {
      from: "/noticias",
      to: "/resource"
    },
    {
      from: "/news/fibra-monterrey-acquires-zeus-real-estate-portfolio-for-",
      to:
        "/news/fibra-monterrey-acquires-zeus-real-estate-portfolio-662m-doubling-portfolio-joining-top-5-reits-mexico/497/lang/en"
    },
    {
      from: "/noticias/certificaciones-sustentables-en-m",
      to: "/noticias/certificaciones-sustentables-mexico/6154"
    },
    {
      from: "/noticias/dos-proyectos-en-san-jer",
      to: "/noticias/proyectos-san-jeronimo-obispado-traeran-desarrollo-mty/514"
    },
    {
      from:
        "/noticias/el-centro-comercial-la-perla-de-fshop-obtiene-la-certificaci",
      to:
        "/noticias/centro-comercial-la-perla-fshop-obtiene-certificacion-edge/546"
    },
    {
      from:
        "/noticias/amazon-expande-operações-no-nordeste-e-inaugura-cd-no-ceará/1392",
      to: "/noticias/amazon-expande-operacoes-nordeste-inaugura-cd-ceara/1392",
      toPlatform: "br"
    },
    {
      from:
        "/noticias/bluemacaw-e-catuaí-gestora-fazem-aquisição-de-r$-364-milhões/1496",
      to: "/noticias/bluemacaw-catuai-gestora-aquisicao-r-364-milhoes/1496",
      toPlatform: "br"
    },
    {
      from:
        "/noticias/comfrio-e-glp-inauguram-usina-solar-em-galpão-logístico-em-são-paulo/1321",
      to:
        "/noticias/comfrio-glp-inauguram-usina-solar-galpao-logistico-sao-paulo/1321",
      toPlatform: "br"
    },
    {
      from: "/noticias/conheça-mais-sobre-certificados-de-sustentabilidade/208",
      to: "/noticias/conheca-mais-certificados-sustentabilidade/208",
      toPlatform: "br"
    },
    {
      from:
        "/noticias/conheça-o-novo-empreendimento-do-complexo-do-pacaembu/287/lang/en",
      to: "/noticias/conheca-novo-empreendimento-complexo-pacaembu/287",
      toPlatform: "br"
    },
    {
      from: "/noticias/conheço-o-primeiro-campus-da-wpp-no-brasil/291",
      to: "/noticias/conheco-primeiro-campus-wpp-brasil/291",
      toPlatform: "br"
    },
    {
      from:
        "/noticias/construção-reclama-de-soluço-no-fornecimento-de-asfalto/687",
      to: "/noticias/construcao-reclama-soluco-fornecimento-asfalto/687",
      toPlatform: "br"
    },
    {
      from:
        "/noticias/cruzeiro-muda-sede-para-coworking-de-empresa-em-crise-mundial/2422",
      to: "/noticias/cruzeiro-muda-sede-coworking-empresa-crise-mundial/2422",
      toPlatform: "br"
    },
    {
      from: "/noticias/dhl-vai-investir-r$-150-milhões-no-brasil-até-2025/694",
      to: "/noticias/dhl-vai-investir-r-150-milhoes-brasil-ate-2025/694",
      toPlatform: "br"
    },
    {
      from:
        "/noticias/e-commerce-dobra-ocupação-dentro-de-condomínios-logísticos/405",
      to: "/noticias/e-commerce-dobra-ocupacao-condominios-logisticos/405",
      toPlatform: "br"
    },
    {
      from:
        "/noticias/giancarlo-nicastro-fala-sobre-tendências-e-movimentos-do-mercado-imobiliário/166",
      to:
        "/noticias/giancarlo-nicastro-fala-tendencias-movimentos-mercado-imobiliario/166",
      toPlatform: "br"
    },
    {
      from: "/noticias/iphan-tomba-provisoriamente-complexo-do-ibirapuera/1386",
      to: "/noticias/iphan-tomba-provisoriamente-complexo-ibirapuera/1386",
      toPlatform: "br"
    },
    {
      from:
        "/noticias/iwg,-de-coworking,-planeja-expandir-no-brasil-com-franquias/2121",
      to: "/noticias/iwg-coworking-planeja-expandir-brasil-franquias/2121",
      toPlatform: "br"
    },
    {
      from: "/noticias/log-finaliza-processo-de-venda-de-ativos/390",
      to: "/noticias/log-finaliza-processo-venda-ativos/390",
      toPlatform: "br"
    },
    {
      from:
        "/noticias/magazine-luiza-pretende-diminuir-tempo-de-entrega-para-algumas-horas/2169",
      to: "/noticias/magazine-luiza-diminuir-tempo-entrega-algumas-horas/2169",
      toPlatform: "br"
    },
    {
      from:
        "/noticias/mercado-de-escritórios-do-rio-de-janeiro-ensaia-retomada/174",
      to: "/noticias/mercado-escritorios-rio-de-janeiro-ensaia-retomada/174",
      toPlatform: "br"
    },
    {
      from: "/noticias/new-siila-client:-tc-formerly-traders-club/6157",
      to: "/noticias/novo-cliente-siila-tc-antigo-traders-club/6157",
      toPlatform: "br"
    },
    {
      from:
        "/noticias/noah-lança-1º-empreendimento-construído-em-madeira-engenheirada-do-brasil/384",
      to:
        "/noticias/noah-lanca-1-empreendimento-madeira-engenheirada-brasil/384",
      toPlatform: "br"
    },
    {
      from:
        "/noticias/novos-entrantes-e-dinâmicas-no-seguimento-de-condomínios-logísticos/470",
      to:
        "/noticias/novos-entrantes-dinamicas-seguimento-condominios-logisticos/470",
      toPlatform: "br"
    },
    {
      from: "/noticias/o-ambicioso-plano-de-expansão-da-oxxo/440",
      to: "/noticias/plano-expansao-oxxo/440",
      toPlatform: "br"
    },
    {
      from:
        "/noticias/público-de-shoppings-está-20--abaixo-do-período-pré-pandemia/381",
      to: "/noticias/publico-shoppings-20-abaixo-pre-pandemia/381",
      toPlatform: "br"
    },
    {
      from:
        "/noticias/perspectivas-para-o-mercado-de-escritórios:-brasil-apresenta-melhor-performance-que-os-eua/146",
      to:
        "/noticias/perspectivas-mercado-escritorios-brasil-apresenta-melhor-performance-eua/146",
      toPlatform: "br"
    },
    {
      from:
        "/noticias/são-paulo,-campinas,-and-brasília-gain-new-office-stock-in-3q21/6164",
      to:
        "/noticias/sao-paulo-campinas-brasilia-ganham-novo-estoque-escritorios-3t21/6164",
      toPlatform: "br"
    },
    {
      from: "/noticias/sony-pictures-is-the-new-tenant-of-thera-corporate/1962",
      to: "/noticias/sony-pictures-nova-ocupante-thera-corporate/1962",
      toPlatform: "br"
    },
    {
      from: "/lang/eN",
      to: "/lang/en"
    },
    {
      from: "/blog/lang/en",
      to: "/resource/lang/en"
    }
  ],
  latam: [
    {
      from: "/privacy-policy/en",
      to: "/privacy-policy"
    },
    {
      from:
        "/news/between-arches-tracks-goodstorage-inaugurates-new-logistics-condominium-sao-paulo-brazil/6609/lang/products/fibra-analytics",
      to:
        "/news/between-arches-tracks-goodstorage-inaugurates-new-logistics-condominium-sao-paulo-brazil/6609"
    },
    {
      from:
        "/news/bgr-b32-reit-announces-shopee-renewal-and-expansion-at-b32-offsetting-banco-master-vacancy/8212/lang/products/fii-data-pro",
      to:
        "/news/bgr-b32-reit-announces-shopee-renewal-and-expansion-at-b32-offsetting-banco-master-vacancy/8212"
    },
    {
      from:
        "/news/borgwarner-invests-47-million-expand-saltillo-plant-generating-450-new-jobs/504/lang/products/fibra-analytics",
      to:
        "/news/borgwarner-invests-47-million-expand-saltillo-plant-generating-450-new-jobs/504"
    },
    {
      from:
        "/news/chucri-zaidan-region-largest-stock-corporate-floors-brazil/192/lang/products/fibra-analytics",
      to: "/news/chucri-zaidan-region-largest-stock-corporate-floors-brazil/192"
    },
    {
      from:
        "/news/construction-industrial-park-cuautitlan-generate-2000-jobs/5165/lang/products/fii-data-pro",
      to:
        "/news/construction-industrial-park-cuautitlan-generate-2000-jobs/5165"
    },
    {
      from:
        "/news/construction-las-plazas-ii-industrial-park-strategic-investments-manufacturing-logistics-state-mexico/7086/lang/products/fii-data-pro",
      to:
        "/news/construction-las-plazas-ii-industrial-park-strategic-investments-manufacturing-logistics-state-mexico/7086"
    },
    {
      from:
        "/news/developing-industrial-properties-brazil/7749/lang/products/fii-data-pro",
      to: "/news/developing-industrial-properties-brazil/7749"
    },
    {
      from:
        "/news/el-palacio-de-hierro-invests-omnichannel-strategy-transform-luxury-shopping-unparalleled-experiences-mexico/6693/lang/products/fii-data-pro",
      to:
        "/news/el-palacio-de-hierro-invests-omnichannel-strategy-transform-luxury-shopping-unparalleled-experiences-mexico/6693"
    },
    {
      from:
        "/news/even-sells-properties-hotel-fasano-itaim-real-estate-fund-r280-million/2581/lang/products/fibra-analytics",
      to:
        "/news/even-sells-properties-hotel-fasano-itaim-real-estate-fund-r280-million/2581"
    },
    {
      from:
        "/news/explore-polanco-exclusive-submarket-mexico-citys-central-business-district/6325/lang/products/fibra-analytics",
      to:
        "/news/explore-polanco-exclusive-submarket-mexico-citys-central-business-district/6325"
    },
    {
      from:
        "/news/fibra-macquarie-secures-150m-sustainable-credit-ifc-world-bank-industrial-projects-mexico/7178/lang/products/fibra-analytics",
      to:
        "/news/fibra-macquarie-secures-150m-sustainable-credit-ifc-world-bank-industrial-projects-mexico/7178"
    },
    {
      from:
        "/news/how-many-docks-industrial-warehouse-need-to-lead-logistics-mexico/7486/lang/products/fibra-analytics",
      to:
        "/news/how-many-docks-industrial-warehouse-need-to-lead-logistics-mexico/7486"
    },
    {
      from:
        "/news/mabe-expands-jalisco-rise-mexican-electronics/7759/lang/products/fii-data-pro",
      to: "/news/mabe-expands-jalisco-rise-mexican-electronics/7759"
    },
    {
      from:
        "/news/metropolitan-arch-of-rio-de-janeiro-highway-didnt-work/7190/lang/products/fii-data-pro",
      to: "/news/metropolitan-arch-of-rio-de-janeiro-highway-didnt-work/7190"
    },
    {
      from:
        "/news/mexico-citys-sustainable-building-certification-program/5177/lang/products/fibra-analytics",
      to: "/news/mexico-citys-sustainable-building-certification-program/5177"
    },
    {
      from:
        "/news/monterrey-thriving-investment-hub-foreign-investments/6396/lang/products/fii-data-pro",
      to: "/news/monterrey-thriving-investment-hub-foreign-investments/6396"
    },
    {
      from:
        "/news/new-industrial-property-sao-paulos-grande-abc-region-delivered-fully-leased-100-occupancy/6414/lang/products/fii-data-pro",
      to:
        "/news/new-industrial-property-sao-paulos-grande-abc-region-delivered-fully-leased-100-occupancy/6414"
    },
    {
      from:
        "/news/private-offices-mexico-city-return-operate-20-percent-capacity-limit/5242/lang/products/fibra-analytics",
      to:
        "/news/private-offices-mexico-city-return-operate-20-percent-capacity-limit/5242"
    },
    {
      from:
        "/news/prologis-park-grande-building-seven-first-industrial-warehouse-latam-boma-gold-wins-sustainability-award/7014/lang/products/fibra-analytics",
      to:
        "/news/prologis-park-grande-building-seven-first-industrial-warehouse-latam-boma-gold-wins-sustainability-award/7014"
    },
    {
      from:
        "/news/rbr-properties-acquires-set-centro-empresarial-mario-garnero/1742/lang/products/fii-data-pro",
      to:
        "/news/rbr-properties-acquires-set-centro-empresarial-mario-garnero/1742"
    },
    {
      from:
        "/news/rugged-tech-installs-queretaro-42-million-pesos/552/lang/products/fii-data-pro",
      to: "/news/rugged-tech-installs-queretaro-42-million-pesos/552"
    },
    {
      from:
        "/news/siila-ceo-presents-industrial-properties-market-overview-iamc-conference-sao-paulo/6663/lang/products/fibra-analytics",
      to:
        "/news/siila-ceo-presents-industrial-properties-market-overview-iamc-conference-sao-paulo/6663"
    },
    {
      from:
        "/news/success-failure-face-ecommerce-5-percent-corporate-space-mexico-allocated-retail-stores/7133/lang/products/fii-data-pro",
      to:
        "/news/success-failure-face-ecommerce-5-percent-corporate-space-mexico-allocated-retail-stores/7133"
    },
    {
      from:
        "/news/tokamptstok-evicted-extrema-business-park-i/189/lang/products/fibra-analytics",
      to: "/news/tokamptstok-evicted-extrema-business-park-i/189"
    },
    {
      from:
        "/news/unlocking-perfect-space-siila-revolutionizes-commercial-property-search-latin-america/6365/lang/products/fibra-analytics",
      to:
        "/news/unlocking-perfect-space-siila-revolutionizes-commercial-property-search-latin-america/6365"
    }
  ],
  co: []
};

/**
 * The rule for `pathname` on `platformId`, or `null`. `pathname` may arrive
 * percent-encoded (`/noticias/conhe%C3%A7a-...`) or not, depending on the
 * client; both are decoded before comparing.
 */
export function findLegacyRedirect(
  platformId: string,
  pathname: string
): LegacyRedirect {
  const path = normalizeLegacyPath(pathname);
  return (
    (LEGACY_REDIRECTS[platformId] || []).find(rule => rule.from === path) ||
    null
  );
}

function normalizeLegacyPath(pathname: string): string {
  let decoded = pathname || "";
  try {
    decoded = decodeURIComponent(decoded);
  } catch (e) {}
  return decoded.replace(/\/+$/, "") || "/";
}
